// @flow
import React, { createRef, Fragment, useCallback, useContext, useEffect } from 'react';
import classnames from 'classnames';
import { forceCheck } from 'react-lazyload';
import { throttle } from 'lodash';

import config from 'Config';
import * as routeBuilder from 'Routes/routeBuilder';

import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { ICONS } from 'Components/utils/icon/icons';
import { NAVIGATION_TRACKING_SEARCH_BUTTON } from 'Components/widgets/navigation/Navigation.tracking';
import { heights as HEIGHTS } from 'Components/widgets/navigation/Navigation.constants';
import { useAutocomplete } from 'Src/hooks/use-autocomplete/useAutocomplete';
import { t } from 'Src/i18n';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import { SearchQuery } from './GlobalSearchQuery.jsx';
import { SearchSuggestions } from './GlobalSearchSuggestions.jsx';

function DesktopSearchContainer({ children, isOpen, offsetTop }) {
  return (
    <aside
      className={classnames('fadeIn fixed inset-0', {
        'invisible opacity-0': !isOpen,
      })}
      style={{
        backgroundColor: 'rgba(1, 1, 1, 0.5)',
        top: `${HEIGHTS.NAVIGATION_LARGE_HEIGHT + offsetTop}px`,
      }}
    >
      {children}
    </aside>
  );
}

const onScroll = throttle(() => {
  forceCheck();
}, 250);

function MobileSearchContainer({ children, isOpen, offsetTop }) {
  return (
    <div
      className={classnames('bg-white fixed bottom-0 rtl:right-0 p-4 overflow-y-auto w-full', {
        'translate-x-0': isOpen,
        'rtl:translate-x-100': !isOpen,
      })}
      onScroll={onScroll}
      style={{
        top: HEIGHTS.NAVIGATION_NARROW_HEIGHT + offsetTop,
        transition: 'transform linear 330ms',
      }}
    >
      {isOpen ? children : null}
    </div>
  );
}

type GlobalSearchProps = {
  className?: string,
  isOpen: boolean,
  offsetTop: number,
  toggleOpen: () => void,
};

export const GlobalSearch = (props: GlobalSearchProps) => {
  const { isOpen, offsetTop, toggleOpen } = props;

  const { isDesktop } = useContext(ResponsiveContext);

  const {
    autocompleteQuery,
    setAutocompleteQuery,
    autocompleteResults,
    setAutocompleteResults,
  } = useAutocomplete();

  // This effect is used to focus the search query input element when the GlobalSearch is opened
  // Use timer to wait for css transition
  const searchInput = createRef();
  useEffect(
    () => {
      let timer = null;

      if (isOpen && searchInput.current) {
        timer = setTimeout(() => {
          searchInput.current && searchInput.current.focus();
        }, 500);
      }

      return () => {
        clearTimeout(timer);
      };
    },
    [isOpen],
  );

  const trackingData = {
    categoryGA: 'headerMenu',
    actionGA: 'header_search',
  };

  const toggleGlobalSearch = useCallback(() => {
    setAutocompleteQuery('');
    setAutocompleteResults([]);
    toggleOpen();
  }, []);

  const onChangeQuery = (e: SyntheticEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setAutocompleteQuery(value);
  };

  const GLOBAL_SEARCH_ENABLED = config.get('featureFlipping.faceting.globalSearchEnabled');

  const searchComponent = (
    <Fragment>
      <SearchQuery
        autocompleteQuery={autocompleteQuery}
        clearGlobalSearch={toggleGlobalSearch}
        onChangeQuery={onChangeQuery}
        ref={searchInput}
        trackingData={trackingData}
      />
      <div
        className={classnames('slideInTop fadeIn w-full bg-white', {
          'h-full': isDesktop,
          'pb-12': !isDesktop,
          'invisible opacity-0': autocompleteQuery.length === 0,
        })}
        data-testid="search-suggestions-container"
        style={
          isDesktop
            ? {
                paddingLeft: '15%',
                paddingRight: '15%',
              }
            : {}
        }
      >
        <SearchSuggestions
          autocompleteQuery={autocompleteQuery}
          autocompleteResults={autocompleteResults}
          clearGlobalSearch={toggleGlobalSearch}
        />
      </div>
    </Fragment>
  );

  const searchLinkLabel = t('faceting.searchLinkLabel');

  return GLOBAL_SEARCH_ENABLED ? (
    <Fragment>
      <TrackClick
        aria-expanded={isOpen}
        aria-label="Search"
        aria-pressed={isOpen}
        as="button"
        className={props.className}
        data-testid="global-search-button"
        onClick={toggleGlobalSearch}
        title={searchLinkLabel}
        trackingData={{
          ...trackingData,
          labelGA: isOpen ? 'close' : 'open',
        }}
        type="button"
        url={routeBuilder.buildUrl('search')}
      >
        <Icon name={ICONS.SEARCH_GLASS} width="1.1875rem" />
        <span className="a11y">{searchLinkLabel}</span>
      </TrackClick>
      {isDesktop ? (
        <DesktopSearchContainer isOpen={isOpen} offsetTop={offsetTop}>
          {searchComponent}
        </DesktopSearchContainer>
      ) : (
        <MobileSearchContainer isOpen={isOpen} offsetTop={offsetTop}>
          {searchComponent}
        </MobileSearchContainer>
      )}
    </Fragment>
  ) : (
    <TrackClick
      as={DynamicLink}
      className={props.className}
      data-testid="global-search-link"
      title={searchLinkLabel}
      trackingData={NAVIGATION_TRACKING_SEARCH_BUTTON}
      url={routeBuilder.buildUrl('search')}
    >
      <Icon name={ICONS.SEARCH_GLASS} width="1.1875rem" />
      <span className="a11y">{searchLinkLabel}</span>
    </TrackClick>
  );
};
